import { useEffect, useState } from "react";

// ─────────────────── UI Components ───────────────────────────
import Modal from "../../common/Modal";
import PrimaryButton, { PrimaryButtonType } from "../../common/PrimaryButton";
import OutlinedButton from "../../common/OutlinedButton";
import Spinner from "../../common/Spinner";
import TiersInformation from "../../../pages/organizations/settings/components/TiersInformation";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";

// ─────────────────── Styles ──────────────────────────────────
import styles from "./OrganizationRequestDetailsModal.module.scss";

// ─────────────────── Store ───────────────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";

// ─────────────────── Types ───────────────────────────────────
import { IOrganizations } from "../../../setup/types/Organisation";

interface Props {
  showModal: boolean;
  closeModal: () => void;
  organization?: IOrganizations;
  /** Opens straight into the reject-reason step (row-level "رفض") instead of
   * the accept/reject buttons (row click / org name — "view details"). */
  initialShowRejectInput?: boolean;
  /** Immediately runs the accept flow once opened (row-level "قبول"), so the
   * fee-setup success step still appears instead of only updating the row. */
  autoAccept?: boolean;
}

const OrganizationRequestDetailsModal = ({
  showModal,
  closeModal,
  organization,
  initialShowRejectInput = false,
  autoAccept = false,
}: Props) => {
  const [showRejectInput, setShowRejectInput] = useState(
    initialShowRejectInput
  );
  const [reason, setReason] = useState("");
  // After a successful accept, the modal switches to letting the admin configure the
  // Waffy Agreement fee template/tiers right away instead of just closing — see
  // TiersInformation below. Configuring it is optional; the admin can always finish it
  // later from the organization's own Settings → "اتفاقية وفّي" tab.
  const [showFeeSetup, setShowFeeSetup] = useState(false);

  const buttonLoading = useOrganisationStore((state) => state.buttonLoading);
  const verifyOrganisation = useOrganisationStore(
    (state) => state.verifyOrganisation
  );
  const rejectOrganisation = useOrganisationStore(
    (state) => state.rejectOrganisation
  );

  // Reset the reject-reason step whenever a different org is opened / the modal closes,
  // respecting the caller's requested initial step (row-level Reject vs. view details).
  useEffect(() => {
    setShowRejectInput(initialShowRejectInput);
    setReason("");
    setShowFeeSetup(false);
  }, [organization?.code, showModal, initialShowRejectInput]);

  const handleClose = () => {
    closeModal();
  };

  const handleAccept = async () => {
    if (!organization?.code) return;
    const accepted = await verifyOrganisation(organization.code);
    if (accepted) {
      setShowFeeSetup(true);
    }
    // On failure, verifyOrganisation already surfaced a toast — keep the modal open on
    // the accept/reject step so the admin can retry instead of silently closing it.
  };

  // Row-level "قبول" opens the modal with autoAccept instead of calling verifyOrganisation
  // directly, so this runs the exact same accept flow (and fee-setup success step) as
  // clicking "قبول" from inside the modal.
  useEffect(() => {
    if (!showModal || !autoAccept || !organization?.code) return;
    handleAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, autoAccept, organization?.code]);

  const handleConfirmReject = async () => {
    if (!organization?.code || !reason.trim()) return;
    await rejectOrganisation(organization.code, reason.trim());
    handleClose();
  };

  const isPending = organization?.verificationStatus === "PENDING";
  const isRejected = organization?.verificationStatus === "REJECTED";

  return (
    <Modal
      size={showFeeSetup ? "large" : "small"}
      customModalClassName={showFeeSetup ? styles.feeSetupModal : undefined}
      visible={showModal}
      onCancel={handleClose}>
      <div className={styles.container}>
        {showFeeSetup ? (
          <>
            <div className={styles.feeSetupHeader}>
              <div className={styles.successBanner}>
                <CheckCircleOutlinedIcon />
                <span>تم قبول طلب {organization?.name} بنجاح</span>
              </div>
              <p>
                يمكنك تحديد رسوم اتفاقية وفّي الآن، أو لاحقًا من صفحة إعدادات
                المؤسسة.
              </p>
            </div>
            <TiersInformation
              organization={organization}
              isEditable
              secondaryAction={
                <OutlinedButton onClick={handleClose}>إغلاق</OutlinedButton>
              }
            />
          </>
        ) : (
          <>
            <div className={styles.header}>
              {organization?.logoUrl && (
                <img
                  src={organization.logoUrl}
                  alt={organization.name}
                  className={styles.logo}
                />
              )}
              <h2>{organization?.name}</h2>
            </div>

            <div className={styles.detailsGrid}>
              <div className={styles.detailRow}>
                <span className={styles.label}>الكود</span>
                <span className={styles.value}>{organization?.code}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>الوصف</span>
                <span className={styles.value}>
                  {organization?.description || "-"}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>الموقع الإلكتروني</span>
                {organization?.websiteUrl ? (
                  <a
                    className={styles.link}
                    href={organization.websiteUrl}
                    target="_blank"
                    rel="noreferrer">
                    {organization.websiteUrl}
                  </a>
                ) : (
                  <span className={styles.value}>-</span>
                )}
              </div>
            </div>

            {isRejected && (
              <div className={styles.rejectedBox}>
                <strong>سبب الرفض</strong>
                <p>{organization?.rejectionReason || "-"}</p>
              </div>
            )}

            {isPending && !showRejectInput && (
              <div className={styles.button_container}>
                <PrimaryButton onClick={handleAccept} disabled={buttonLoading}>
                  {buttonLoading ? <Spinner /> : "قبول"}
                </PrimaryButton>
                <PrimaryButton
                  buttonType={PrimaryButtonType.DANGER}
                  disabled={buttonLoading}
                  onClick={() => setShowRejectInput(true)}>
                  رفض
                </PrimaryButton>
              </div>
            )}

            {isPending && showRejectInput && (
              <>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="اكتب سبب رفض الطلب..."
                  autoFocus
                />
                <div className={styles.button_container}>
                  <PrimaryButton
                    buttonType={PrimaryButtonType.DANGER}
                    disabled={buttonLoading || !reason.trim()}
                    onClick={handleConfirmReject}>
                    {buttonLoading ? <Spinner /> : "تأكيد الرفض"}
                  </PrimaryButton>
                  <OutlinedButton
                    disabled={buttonLoading}
                    onClick={() => setShowRejectInput(false)}>
                    إلغاء
                  </OutlinedButton>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default OrganizationRequestDetailsModal;
